"use client";

import clsx from "clsx";
import type {TFunction} from "i18next";
import ApprovalReviewScrollArea from "./ApprovalReviewScrollArea";
import {useId, useLayoutEffect, useRef, useState} from "react";
import ApprovalDecisionStatus, {selectionClass, selectionLabelKey} from "./ApprovalDecisionStatus";
import {proposalTitle} from "./proposalIdentity";
import type {ApprovalDecisionOutcome, ApprovalPosition} from "@/types/chatMessages";
import type {MutationProposal} from "@/services/wingman/mutation-proposal";
import UseCaseSnapshotDiff from "./UseCaseSnapshotDiff";
import ScriptSnapshotDiff from "./ScriptSnapshotDiff";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {useTranslation} from "react-i18next";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import ToolApprovalNotices from "./ToolApprovalNotices";
import {useToolApprovalDecision} from "./useToolApprovalDecision";
import ToolDetails from "./ToolDetails";
import {approvalPresentation, approvalStatusKey} from "./approvalPresentation";
import {displayToolPresentation} from "@repo/ui/gailgpt/tool-display";
import {isPresentationRecord, presentationText} from "@repo/ui/gailgpt/tool-presentation";

interface ToolApprovalCardProps {
   position?: ApprovalPosition;
   /** Stacked reviews fold approved cards while retaining an inspectable summary. */
   collapseOnApproval?: boolean;
   embedded?: boolean;
   panel?: boolean;
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
}

export default function ToolApprovalCard({embedded = false, panel = false, position, collapseOnApproval = false, call, completed, result, active, disabled, onRespond, onRequestFresh}: ToolApprovalCardProps) {
   const {t} = useTranslation("ask");
   const targetId = useId();
   const contentId = useId();
   const decision = useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh});
   const {status, settled, expired, error, ownerOnly, saved, queued, canRequestFresh, requestFresh, requestedFresh} = decision;
   const collapse = useApprovalCollapse({collapseOnApproval, panel, approval: call.approval, decision});
   const {collapsed} = collapse;
   const approval = call.approval;
   if (!approval) return null;

   const model = approvalCardPresentation(call, approval, status, position, t);
   const {unavailable, structured, title, statusKey} = model;
   const notices = <ToolApprovalNotices status={status} settled={settled} expired={expired} error={error} ownerOnly={ownerOnly} canRequestFresh={canRequestFresh} onRequestFresh={requestFresh} requestedFresh={requestedFresh}
      previewUnavailable={unavailable} queued={queued} remaining={position?.remaining} operation={structured ? "execution" : undefined} className="mx-4 my-3 text-sm" />;

   const decisionControls = <ToolDecisionControls decision={decision} model={model} active={active} position={position}
      targetId={targetId} collapseOnApproval={collapseOnApproval} approve={collapse.approve} />;

   return (
      <section aria-label={t(structured ? "toolApproval.execution.title" : "toolApproval.title")} className={clsx(cardLayoutClass(panel, embedded), selectionClass(queued) ?? "border-neutral bg-panel", structured && !panel && !collapseOnApproval && "max-w-xl")}>
         <ToolCardHeader model={model} call={call} decision={decision} collapse={collapse}
            collapseOnApproval={collapseOnApproval} panel={panel} embedded={embedded} contentId={contentId} />
         {panel && <div className="shrink-0 border-b border-neutral px-4 py-3 text-xs">
            <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
         </div>}
         <div id={contentId} hidden={collapsed} className={panel ? "contents" : undefined}>
            {collapseOnApproval && decisionControls}
            {/* Keep long reviews keyboard-scrollable while the decision buttons stay outside the scroll region. */}
            <ApprovalReviewScrollArea enabled={panel} label={title}>
               <ToolReviewContent call={call} saved={saved} model={model} />
               {!panel && notices}
            </ApprovalReviewScrollArea>
            {panel && <div className="max-h-[30%] shrink-0 overflow-y-auto">{notices}</div>}
            {!collapseOnApproval && decisionControls}
         </div>
      </section>
   );
}

function ToolApprovalContent({call, saved}: {call: MessageContentFunctionCall; saved?: Record<string, unknown> | null}) {
   const approval = call.approval;
   if (!approval) return null;
   if (approval.proposal) {
      if (approval.proposal.capability === "use_cases_update") return <UseCaseSnapshotDiff preview={approval.proposal} saved={saved} />;
      return <ScriptSnapshotDiff preview={approval.proposal} />;
   }
   return null;
}

type ApprovalDecision = ReturnType<typeof useToolApprovalDecision>;
type CardPresentation = ReturnType<typeof approvalCardPresentation>;

function decisionLabelKeys(batch: boolean, structured: boolean) {
   if (batch) return {approve: "toolApproval.batch.approveThis", deny: "toolApproval.batch.rejectThis"};
   if (structured) return {approve: "toolApproval.execution.approve", deny: "toolApproval.execution.cancel"};
   return {approve: "toolApproval.apply", deny: "toolApproval.discard"};
}

function approvalCardPresentation(call: MessageContentFunctionCall, approval: NonNullable<MessageContentFunctionCall["approval"]>, status: ApprovalDecision["status"], position: ApprovalPosition | undefined, t: TFunction<"ask">) {
   const {presentation: validated, unavailable} = approvalPresentation(call);
   const presentation = validated ? displayToolPresentation(validated, call.parameters, t) : undefined;
   const structured = !approval.proposal && !approval.previewUnavailable;
   let title = call.functionName;
   if (structured && presentation) title = presentationText(presentation.title, t);
   if (approval.proposal) title = proposalTitle(approval.proposal);
   const statusKey = approvalStatusKey(call, status);
   const labels = decisionLabelKeys(!!position, structured);
   const approveLabel = !position && structured && presentation?.approveLabel
      ? presentationText(presentation.approveLabel, t) : t(labels.approve);
   const denyLabel = t(labels.deny);
   const body = isPresentationRecord(call.parameters.body) ? call.parameters.body : call.parameters;
   const subject = [body.title, body.name].find((value): value is string => typeof value === "string" && !!value.trim()) ?? title;
   let description = t("toolApproval.previewDescription");
   if (structured) description = presentation?.description ? presentationText(presentation.description, t) : t("toolApproval.execution.description");

   return {presentation, unavailable, structured, title, statusKey, approveLabel, denyLabel, subject, description};
}

function cardLayoutClass(panel: boolean, embedded: boolean) {
   if (panel) return "flex h-full min-h-0 flex-col overflow-hidden";
   if (embedded) return "overflow-clip border-t";
   return "my-3 overflow-clip rounded-xl border";
}

function useApprovalCollapse({collapseOnApproval, panel, approval, decision}: {
   collapseOnApproval: boolean;
   panel: boolean;
   approval: MessageContentFunctionCall["approval"];
   decision: ApprovalDecision;
}) {
   const summaryRef = useRef<HTMLButtonElement>(null);
   const focusSummary = useRef(false);
   const [expanded, setExpanded] = useState(false);
   const {status, error, expired, queued, draft} = decision;
   const collapsible = collapseOnApproval && !panel && !error && !expired &&
      (queued === true || (status === "submitting" && draft === true) || approval?.approved === true || status === "completed") &&
      ["required", "submitting", "approved", "completed"].includes(status);
   const collapsed = collapsible && !expanded;
   useLayoutEffect(() => {
      if (!collapsed || !focusSummary.current) return;
      focusSummary.current = false;
      summaryRef.current?.focus({preventScroll: true});
      // Scroll only the review stack, without moving the surrounding conversation.
      const summary = summaryRef.current;
      if (!summary) return;
      for (let parent = summary.parentElement; parent; parent = parent.parentElement) {
         if (!/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) continue;
         const item = summary.getBoundingClientRect();
         const viewport = parent.getBoundingClientRect();
         if (item.top < viewport.top) parent.scrollTop += item.top - viewport.top;
         else if (item.bottom > viewport.bottom) parent.scrollTop += item.bottom - viewport.bottom;
         break;
      }
   }, [collapsed]);
   const approve = async () => {
      focusSummary.current = collapseOnApproval;
      const outcome = await decision.respond(true);
      if (outcome === "queued" || outcome === "submitted") setExpanded(false);
      else focusSummary.current = false;
   };

   return {collapsible, collapsed, summaryRef, setExpanded, approve};
}

function ToolCardHeading({call, model, decision, collapseOnApproval}: {
   call: MessageContentFunctionCall;
   model: CardPresentation;
   decision: ApprovalDecision;
   collapseOnApproval: boolean;
}) {
   const {structured, title, subject, statusKey} = model;
   const {queued, status} = decision;
   const useCase = structured && call.functionName === "use_cases_create";
   if (useCase && collapseOnApproval) return <>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-500">
         <span>{title}</span>
         <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
      </div>
      <h3 className="break-words text-base font-semibold leading-snug tracking-tight text-900">{subject}</h3>
   </>;
   if (structured) return <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h3 className="min-w-0 break-words text-sm font-semibold leading-relaxed text-900">{title}</h3>
      <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
   </div>;
   return <>
      <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
      <span className="mt-1 block break-words text-sm font-semibold text-800">{title}</span>
   </>;
}

function ToolCardHeader({model, call, decision, collapse, collapseOnApproval, panel, embedded, contentId}: {
   model: CardPresentation;
   call: MessageContentFunctionCall;
   decision: ApprovalDecision;
   collapse: ReturnType<typeof useApprovalCollapse>;
   collapseOnApproval: boolean;
   panel: boolean;
   embedded: boolean;
   contentId: string;
}) {
   const {structured, subject, statusKey} = model;
   const {queued, status} = decision;
   const {collapsible, collapsed, summaryRef, setExpanded} = collapse;
   if (collapsible) return <button ref={summaryRef} type="button" aria-expanded={!collapsed} aria-controls={contentId}
      onClick={() => setExpanded(value => !value)}
      className="flex w-full items-start gap-3 px-5 py-4 text-left hover:bg-100/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
         <span className="break-words text-sm font-semibold leading-relaxed text-900">{subject}</span>
         <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
      </span>
      <Icon icon={collapsed ? "chevron-down" : "chevron-up"} className="mt-1 h-3 w-3 shrink-0 text-500" />
   </button>;
   if (panel || (!structured && embedded)) return null;
   return <div className={structured ? "px-5 pb-2 pt-4" : "border-b border-neutral px-4 py-3"}><ToolCardHeading call={call} model={model} decision={decision} collapseOnApproval={collapseOnApproval} /></div>;
}

function ToolDecisionControls({decision, model, active, position, targetId, collapseOnApproval, approve}: {
   decision: ApprovalDecision;
   model: CardPresentation;
   active: boolean;
   position?: ApprovalPosition;
   targetId: string;
   collapseOnApproval: boolean;
   approve: () => Promise<void>;
}) {
   const {t} = useTranslation("ask");
   const {queued, canRespond, canApply, respond} = decision;
   const {unavailable, denyLabel, approveLabel, description} = model;
   let decisionDescription = description;
   if (position) decisionDescription = t(position.remaining === 1 && queued == null ? "toolApproval.batch.finalDecision" : "toolApproval.batch.singleDescription", {count: position.total});
   if (decision.settled || !active) return null;
   return (
      <div className={clsx("shrink-0 border-neutral bg-panel px-5 py-3", collapseOnApproval ? "sticky top-0 z-[1] border-b" : "border-t")}>
         <fieldset aria-labelledby={position ? targetId : undefined} className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            {position && <p id={targetId} className="mr-auto text-xs font-medium text-500">{t("toolApproval.batch.position", position)}</p>}
            <Button type="tertiary" compact disabled={!canRespond} aria-pressed={queued === false} onClick={() => void respond(false)}
               className="aria-pressed:bg-100 aria-pressed:text-600"
               iconBefore={queued === false ? <Icon icon="xmark" className="h-3 w-3" /> : undefined}>
               {queued === false ? t(selectionLabelKey(false)) : denyLabel}
            </Button>
            <Button type={queued === true ? "tertiary" : "primary"} compact disabled={!canApply} aria-pressed={queued === true} onClick={() => void approve()}
               className="aria-pressed:bg-100 aria-pressed:text-600"
               iconBefore={<Icon icon="check" className="h-3 w-3" />}>
               {queued === true ? t(selectionLabelKey(true)) : approveLabel}
            </Button>
         </fieldset>
         {!unavailable && <p className="mt-3 text-xs leading-relaxed text-500">{decisionDescription}</p>}
      </div>
   );
}

function ToolReviewContent({call, saved, model}: {
   call: MessageContentFunctionCall;
   saved: ApprovalDecision["saved"];
   model: CardPresentation;
}) {
   if (!model.structured) return <ToolApprovalContent call={call} saved={saved} />;
   if (model.unavailable || !model.presentation) return null;
   return <ToolDetails presentation={model.presentation} parameters={call.parameters} />;
}
